/* eslint-disable jsx-a11y/label-has-for, jsx-a11y/label-has-associated-control */
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import axios from 'axios';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';
import Recaptcha from 'react-recaptcha';
import { RECAPTCHA_SITE_KEY } from '../utils/global';

function ContactModal({ contactPopup, contactPopupClick }) {
  const [input, setInput] = useState({
    fname: '',
    email: '',
    phone: '',
    message: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [recaptchaResponse, setRecaptchaResponse] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);

  const inputChangeHandler = useCallback((e) => {
    const { value, name } = e.target;

    setInput((state) => ({ ...state, [name]: value }));
  }, []);

  const validateInput = useCallback(() => {
    const newErrors = [];

    if (input.email.length > 0 && !isEmail(input.email))
      newErrors.push({
        name: 'email',
        message: 'Please enter a valid email address.',
      });

    if (input.phone.length === 0)
      newErrors.push({
        name: 'phone',
        message: 'Please enter a phone number.',
      });

    if (input.phone.length > 0 && !isMobilePhone(input.phone))
      newErrors.push({
        name: 'phone',
        message: 'Please enter a valid phone number.',
      });

    if (input.message.length > 10000)
      newErrors.push({
        name: 'message',
        message: 'Please enter your message within 10,000 characters.',
      });

    if (recaptchaResponse === null)
      newErrors.push({
        name: 'message',
        message: 'Please perform captcha.',
      });

    setErrors(newErrors);

    return newErrors.length === 0;
  }, [input, recaptchaResponse]);

  const verifyRecaptchaCallback = useCallback((payload) => {
    setRecaptchaResponse(payload);
  }, []);

  const getFormType = useCallback(() => {
    if (typeof contactPopup === 'object') return contactPopup.formType;
    if (typeof contactPopup === 'string') return contactPopup;
    return 'contact';
  }, [contactPopup]);

  const submitHandler = useCallback(
    (e) => {
      e.preventDefault();

      if (validateInput()) {
        setIsSending(true);
        axios
          .post('https://api.algobulls.com/v3/contact', {
            formType: getFormType(),
            name: input.fname,
            email: input.email,
            phoneNumber: input.phone,
            message: input.message,
            captcha: recaptchaResponse,
          })
          .then((res) => {
            setShowSuccess(true);
            setIsSending(false);
            setInput({ fname: '', email: '', phone: '', message: '' });
            setApiResponse(res);
          })
          .catch((err) => {
            setErrors([
              {
                name: 'message',
                message: err?.response?.data?.details ?? err.toString(),
              },
            ]);
            setIsSending(false);
          });
      }
    },
    [input, recaptchaResponse],
  );

  const clickHandler = useCallback(
    (e) => {
      if (e.target.classList.contains('contact-pop-main')) contactPopupClick(e);
    },
    [contactPopupClick],
  );

  const keyDownHandler = useCallback(
    (e) => {
      if (e.key === 'Escape') contactPopupClick(e);
    },
    [contactPopupClick],
  );

  useEffect(() => {
    if (contactPopup === false) setShowSuccess(false);
  }, [contactPopup]);

  useEffect(() => {
    window.addEventListener('click', clickHandler);

    return () => window.removeEventListener('click', clickHandler);
  }, [clickHandler]);

  useEffect(() => {
    window.addEventListener('keydown', keyDownHandler);

    return () => window.removeEventListener('keydown', keyDownHandler);
  }, [keyDownHandler]);

  return (
    <div className={`contact-pop-main${contactPopup ? ' contact-show' : ''}`}>
      <div className="contact-pop-block">
        <button
          type="button"
          className="contact-close"
          aria-label="contact close button"
          onClick={contactPopupClick}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              opacity="0.6"
              d="M15 1L1 15M1 1L15 15L1 1Z"
              stroke="#213654"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {showSuccess === false && (
          <div className="form-area">
            <h2>
              {typeof contactPopup === 'string'
                ? contactPopup
                : contactPopup.title || 'Contact Us'}
            </h2>
            <form onSubmit={submitHandler}>
              <div className="form-group">
                <input
                  type="text"
                  id="contact-name"
                  name="fname"
                  className={`contact-input${
                    input.fname.length > 0 ? ' is-filled' : ''
                  }`}
                  required
                  value={input.fname}
                  onChange={inputChangeHandler}
                />
                <label required htmlFor="contact-name">
                  Name
                </label>
              </div>
              <div className="form-group">
                <input
                  type="email"
                  id="contact-email"
                  className={`contact-input${
                    input.email.length > 0 ? ' is-filled' : ''
                  }`}
                  name="email"
                  value={input.email}
                  onChange={inputChangeHandler}
                  required={getFormType() !== 'generalQuery'}
                />
                <label
                  required={getFormType() !== 'generalQuery'}
                  htmlFor="contact-email">
                  Email
                </label>
              </div>
              <div className="form-group">
                <input
                  type="text"
                  id="contact-phone"
                  className={`contact-input${
                    input.phone.length > 0 ? ' is-filled' : ''
                  }`}
                  name="phone"
                  value={input.phone}
                  onChange={inputChangeHandler}
                  required
                />
                <label required htmlFor="contact-phone">
                  Phone
                </label>
              </div>
              <div className="form-group">
                <textarea
                  id="contact-message"
                  className={`contact-input${
                    input.message.length > 0 ? ' is-filled' : ''
                  }`}
                  rows="2"
                  name="message"
                  value={input.message}
                  onChange={inputChangeHandler}
                  maxLength="10000"
                  required
                />
                <label required htmlFor="contact-message">
                  Message
                </label>
              </div>
              <div className="form-group">
                {contactPopup && (
                  <Recaptcha
                    sitekey={RECAPTCHA_SITE_KEY}
                    verifyCallback={verifyRecaptchaCallback}
                  />
                )}
              </div>
              <button className="button" type="submit" disabled={isSending}>
                {isSending ? 'Submitting' : 'Send Message'}
              </button>
              {errors.length > 0 &&
                errors.map(({ name, message }) => (
                  <p key={name} style={{ marginTop: 10, color: 'red' }}>
                    {message}
                  </p>
                ))}
            </form>
          </div>
        )}

        {showSuccess && (
          <div className="thanks-area">
            <img src="/img/circle-check-big.svg" alt="check" />
            <h3>Thank You</h3>
            <p style={{ padding: '0px 20px' }}>
              {apiResponse?.data?.message ??
                'We will get back to you very soon'}
            </p>
            <Link
              to="/"
              className="button-unstyled"
              onClick={contactPopupClick}>
              Back to home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

ContactModal.propTypes = {
  contactPopup: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
  contactPopupClick: PropTypes.func.isRequired,
};

export default ContactModal;
